import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, X } from "lucide-react";
import { toast } from "sonner";
import adminApi, { apiError } from "@/lib/adminApi";
import { Panel, inputCls, money, shortDate, btnPrimary, btnSecondary } from "@/components/admin/ui";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const emptyForm = { first_name: "", last_name: "", company: "", email: "", phone: "", street: "", house_num: "", pcode: "", city: "", country: "CH" };

export default function AdminCustomers() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      adminApi.get("/customers", { params: { q, page, per_page: 25 } })
        .then((r) => setData(r.data)).catch(() => {});
    }, q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [q, page, reload]);

  useEffect(() => { setPage(1); }, [q]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      await adminApi.post("/customers", form);
      toast.success("Client créé");
      setShowNew(false);
      setForm(emptyForm);
      setReload((r) => r + 1);
    } catch (err) {
      toast.error(apiError(err, "Création impossible"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink">Clients</h1>
          <p className="mt-0.5 text-sm text-slate">CRM particuliers — fiches créées automatiquement à la première réservation.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-64">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate" strokeWidth={1.5} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nom, e-mail, téléphone…"
              data-testid="customers-search" className={`${inputCls} pl-9`} />
          </div>
          <button type="button" onClick={() => setShowNew(true)} data-testid="new-customer-button" className={btnPrimary}>
            <Plus className="h-4 w-4" strokeWidth={2} /> Nouveau client
          </button>
        </div>
      </div>

      <Panel testId="customers-list">
        {!data && <div className="h-40 animate-pulse rounded bg-cloud" />}
        {data && data.customers.length === 0 && <p className="py-8 text-center text-sm text-slate" data-testid="customers-empty">Aucun client pour le moment.</p>}
        {data && data.customers.length > 0 && (
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
            {data.customers.map((c) => (
              <Link key={c.id} to={`/admin/app/customers/${c.id}`} data-testid={`customer-card-${c.id}`}
                className="rounded border border-slate/15 bg-white p-4 transition-colors hover:border-miles/40">
                <div className="flex items-center justify-between">
                  <p className="font-semibold text-ink">{c.first_name} {c.last_name}</p>
                  {(c.tags || []).slice(0, 2).map((t) => (
                    <span key={t} className="rounded bg-miles-ice px-1.5 py-0.5 text-[10px] font-semibold text-miles">{t}</span>
                  ))}
                </div>
                <p className="mt-1 truncate text-sm text-slate">{c.email || c.phone}</p>
                <div className="mt-3 flex items-center justify-between font-mono text-xs text-slate">
                  <span>{c.bookings_count} course(s)</span>
                  <span className="font-semibold text-ink">{money(c.revenue_total)}</span>
                  <span>depuis {shortDate(c.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
        {data && data.total > 25 && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-slate">{data.total} clients</span>
            <div className="flex gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border border-slate/25 px-3 py-1.5 disabled:opacity-40">←</button>
              <button type="button" disabled={page * 25 >= data.total} onClick={() => setPage(page + 1)} className="rounded border border-slate/25 px-3 py-1.5 disabled:opacity-40">→</button>
            </div>
          </div>
        )}
      </Panel>

      {showNew && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-ink/40 p-4" data-testid="new-customer-modal"
          onClick={() => setShowNew(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-ink">Nouveau client</h2>
              <button type="button" onClick={() => setShowNew(false)} data-testid="new-customer-close"
                className="rounded-lg p-1.5 text-slate transition-colors hover:bg-cloud"><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate">Prénom</label>
                  <input value={form.first_name} onChange={set("first_name")} data-testid="nc-first-name" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Nom</label>
                  <input value={form.last_name} onChange={set("last_name")} data-testid="nc-last-name" className={`${inputCls} mt-1`} /></div>
              </div>
              <div><label className="text-xs font-medium text-slate">Société <span className="font-normal">(facultatif)</span></label>
                <input value={form.company} onChange={set("company")} data-testid="nc-company" className={`${inputCls} mt-1`} placeholder="Hôtel Président Wilson" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate">E-mail</label>
                  <input type="email" value={form.email} onChange={set("email")} data-testid="nc-email" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Téléphone</label>
                  <input value={form.phone} onChange={set("phone")} data-testid="nc-phone" className={`${inputCls} mt-1`} placeholder="+41…" /></div>
              </div>
              <div className="grid grid-cols-[1fr_90px] gap-3">
                <div><label className="text-xs font-medium text-slate">Adresse</label>
                  <AddressAutocomplete value={form.street} testId="nc-street" inputClass={`${inputCls} mt-1`}
                    placeholder="Commencez à taper…" onChange={(v) => setForm({ ...form, street: v })}
                    selectValue={(item) => item.street || item.name || item.label}
                    onSelect={(item) => item && setForm((f) => ({
                      ...f, house_num: item.housenumber || f.house_num, pcode: item.postcode || f.pcode,
                      city: item.city || f.city, country: (item.countrycode || f.country || "CH").toUpperCase(),
                    }))} /></div>
                <div><label className="text-xs font-medium text-slate">N°</label>
                  <input value={form.house_num} onChange={set("house_num")} data-testid="nc-housenum" className={`${inputCls} mt-1`} /></div>
              </div>
              <div className="grid grid-cols-[90px_1fr_70px] gap-3">
                <div><label className="text-xs font-medium text-slate">NPA</label>
                  <input value={form.pcode} onChange={set("pcode")} data-testid="nc-pcode" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Localité</label>
                  <input value={form.city} onChange={set("city")} data-testid="nc-city" className={`${inputCls} mt-1`} /></div>
                <div><label className="text-xs font-medium text-slate">Pays</label>
                  <input value={form.country} onChange={set("country")} data-testid="nc-country" className={`${inputCls} mt-1 uppercase`} maxLength={2} /></div>
              </div>
              <p className="text-[11px] text-slate">Nom, prénom ou société requis. L&apos;adresse se complète toute seule dès que vous la choisissez dans les suggestions.</p>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowNew(false)} className={btnSecondary}>Annuler</button>
              <button type="submit" disabled={saving} data-testid="nc-save" className={`${btnPrimary} disabled:opacity-50`}>
                {saving ? "Création…" : "Créer le client"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
